import * as React from 'react';
import { Tabs as MUITabs } from '@mui/material';
import { TabsCustomProps } from './Tabs.types';
import Flex from '@components/Flex/Flex';
import { StyledTabPanel, StyledTabs } from './Tabs.styles';

interface TabPanelProps {
  children?: React.ReactNode;
  currentValue: string | number;
  value: string | number;
}

const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  currentValue,
}) => {
  return (
    <StyledTabPanel
      role='tabpanel'
      hidden={value !== currentValue}
      id={`tabPanel-tab-${value}`}
      data-testid={`tabPanel-tab-${value}`}
      shouldShow={value === currentValue}
    >
      <Flex flex={1} width='100%'>
        {value === currentValue && children}
      </Flex>
    </StyledTabPanel>
  );
};

const Tabs: React.FC<TabsCustomProps> = ({
  tabItem,
  tabRightComponent: TabRightComponent,
  ...tabProps
}) => {
  const [value, setValue] = React.useState(0);

  React.useEffect(() => {
    setValue(tabProps.value ?? 0);
  }, [tabProps.value]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Flex
      sx={{
        flex: '1 0 auto',
        maxWidth: '100%',
        flexDirection: tabProps.orientation === 'vertical' ? 'row' : 'column',
      }}
    >
      <Flex>
        <MUITabs
          style={{ flex: 1 }}
          value={value}
          onChange={handleChange}
          {...tabProps}
          sx={{ pr: { md: 8 } }}
          TabScrollButtonProps={{
            sx: {
              '&.Mui-disabled': {
                display: 'none',
                width: 0,
              },
            },
          }}
        >
          {tabItem.map((tab) => (
            <StyledTabs
              key={tab.id}
              label={tab.label}
              disableRipple
              data-testid={`tab-${tab.id}`}
              {...tab.tabProps}
            />
          ))}
        </MUITabs>
        {TabRightComponent && <TabRightComponent />}
      </Flex>
      {tabItem.map((tab, index) => (
        <TabPanel
          key={index}
          value={tab.tabProps?.value ?? index}
          currentValue={tabProps.value ?? value}
        >
          {tab.component}
        </TabPanel>
      ))}
    </Flex>
  );
};

export default Tabs;
